/**
 * "Ausente" (docs/spec/friends.md): sem mexer nesta janela por `FRIENDS_LIMITS.awayAfterMs`, ela
 * avisa o servidor; mexeu de novo, avisa que voltou. Cada janela (a principal e as destacadas)
 * informa por si; o servidor só mostra "Ausente" quando todas estão sem uso.
 */
import { useEffect } from 'react';
import { FRIENDS_LIMITS } from '../shared.js';
import { useApp } from '../store.js';
import { createIdleClock } from './friendsModel.js';

/** Quanto tempo entre uma conferida e outra. */
const CHECK_MS = 30_000;

/** Liga o aviso de ausente desta janela (montado uma vez, na casca logada e nas janelas destacadas). */
export function useAwayReporter(): void {
  useEffect(() => {
    const clock = createIdleClock(FRIENDS_LIMITS.awayAfterMs);
    const report = useApp.getState().reportAway;
    report(false);
    let lastMove = 0;
    const activity = () => {
      if (clock.activity(Date.now())) report(false);
    };
    // o mouse passando conta, mas no máximo uma vez por segundo
    const move = () => {
      const now = Date.now();
      if (now - lastMove < 1000) return;
      lastMove = now;
      activity();
    };
    const visible = () => {
      if (!document.hidden) activity();
    };
    const timer = window.setInterval(() => {
      if (clock.tick(Date.now())) report(true);
    }, CHECK_MS);
    const events = ['pointerdown', 'keydown', 'wheel', 'touchstart', 'focus'] as const;
    for (const name of events) window.addEventListener(name, activity, { passive: true });
    window.addEventListener('pointermove', move, { passive: true });
    document.addEventListener('visibilitychange', visible);
    return () => {
      window.clearInterval(timer);
      for (const name of events) window.removeEventListener(name, activity);
      window.removeEventListener('pointermove', move);
      document.removeEventListener('visibilitychange', visible);
    };
  }, []);
}
